import {
  initMap,
  estimateTiles,
  DEFAULT_POSTER_TONE,
  type BoundingBox,
  type LandmarkDisplay,
  type PosterTone,
} from "./map";
import { fetchLandmarks, rankByFame, sortLandmarks, type Landmark } from "./landmarks";
import { searchCities, type CityResult } from "./search";

const WORKER_URL = (import.meta.env.VITE_WORKER_URL || "").replace(/\/$/, "");

// Must match the Worker's cap. After the del-canvas-before-thumbnailing fix,
// 2000 tiles → ~45K×45K → 7.6 GB peak → safe on GHA's 16 GB runner.
const MAX_TILES = 2000;
const SEARCH_DEBOUNCE_MS = 250;
// Target zoom when flying to a searched city. 13.5 matches the default Porto
// view and the framing of the IKONOSPACE Barcelona reference poster.
const CITY_ZOOM = 13.5;
// Display cap. The ranking puts the most globally-famous first, so 50 is
// more than enough for poster design. Keeps the list scannable and the
// per-draft localStorage cache small.
const DISPLAY_LANDMARK_CAP = 50;

interface JobStatus {
  status:
    | "queued"
    | "started"
    | "downloading"
    | "stitching"
    | "encoding"
    | "uploading"
    | "complete"
    | "failed";
  progress?: number;
  total?: number;
  urls?: {
    color: string;
    bw: string;
    color_preview: string;
    bw_preview: string;
    overlay_svg?: string;
  };
  error?: string;
  name?: string;
  zoom?: number;
  step?: string;
  detail?: string;
}

function el<T extends HTMLElement>(id: string): T {
  const e = document.getElementById(id);
  if (!e) throw new Error(`Missing element #${id}`);
  return e as T;
}

function bootstrap() {
  const mapEl = el<HTMLDivElement>("map");
  const handle = initMap(mapEl);

  const frame = el<HTMLDivElement>("preview-frame");
  const posterTitle = el<HTMLDivElement>("poster-title");
  const bwToggle = el<HTMLInputElement>("bw-toggle");
  const labelsToggle = el<HTMLInputElement>("labels-toggle");
  const lockToggle = el<HTMLInputElement>("lock-toggle");
  const marginToggle = el<HTMLInputElement>("margin-toggle");
  const orientationBtns = document.querySelectorAll<HTMLButtonElement>(
    ".toggle-btn[data-orientation]",
  );

  const form = el<HTMLFormElement>("generate-form");
  const nameInput = el<HTMLInputElement>("city-name");
  const zoomSelect = el<HTMLSelectElement>("zoom");
  const bboxOutput = el<HTMLOutputElement>("bbox-output");
  const tileEstimate = el<HTMLOutputElement>("tile-estimate");
  const generateBtn = el<HTMLButtonElement>("generate-btn");

  const statusSection = el<HTMLElement>("job-status");
  const statusText = el<HTMLParagraphElement>("status-text");
  const statusHint = el<HTMLParagraphElement>("status-hint");
  const progressFill = el<HTMLDivElement>("progress-fill");

  const resultsSection = el<HTMLElement>("results");
  const previewColor = el<HTMLImageElement>("preview-color");
  const previewBw = el<HTMLImageElement>("preview-bw");
  const downloadColor = el<HTMLAnchorElement>("download-color");
  const downloadBw = el<HTMLAnchorElement>("download-bw");
  const downloadOverlay = el<HTMLAnchorElement>("download-overlay");
  const overlayRow = el<HTMLParagraphElement>("overlay-row");
  const regenSvgBtn = el<HTMLButtonElement>("regen-svg-btn");
  const regenSvgStatus = el<HTMLSpanElement>("regen-svg-status");

  const landmarksList = el<HTMLDivElement>("landmarks-list");
  const landmarksStatus = el<HTMLSpanElement>("landmarks-status");
  const landmarksClear = el<HTMLButtonElement>("landmarks-clear");
  const landmarksLoad = el<HTMLButtonElement>("landmarks-load");
  const landmarksAdd = el<HTMLButtonElement>("landmarks-add");
  const showAllDotsToggle = el<HTMLInputElement>("show-all-dots-toggle");
  const legendModeToggle = el<HTMLInputElement>("legend-mode-toggle");
  const titleSizeSlider = el<HTMLInputElement>("title-size-slider");
  const titleSizeOut = el<HTMLOutputElement>("title-size-out");
  const titleColorInput = el<HTMLInputElement>("title-color-input");
  const landmarkSizeSlider = el<HTMLInputElement>("landmark-size-slider");
  const landmarkSizeOut = el<HTMLOutputElement>("landmark-size-out");
  const landmarkColorInput = el<HTMLInputElement>("landmark-color-input");
  const posterLegend = el<HTMLDivElement>("poster-legend");

  const gallery = el<HTMLDivElement>("gallery");
  const refreshGallery = el<HTMLButtonElement>("refresh-gallery");
  const saveDraftBtn = el<HTMLButtonElement>("save-draft-btn");

  const citySearch = el<HTMLInputElement>("city-search");
  const searchResults = el<HTMLDivElement>("search-results");

  // Tone slider bindings — [input, output, tone key]
  const toneSliders: Array<[HTMLInputElement, HTMLOutputElement, keyof PosterTone]> = [
    [el("tone-brightness-min"), el("tone-brightness-min-out"), "brightnessMin"],
    [el("tone-brightness-max"), el("tone-brightness-max-out"), "brightnessMax"],
    [el("tone-contrast"), el("tone-contrast-out"), "contrast"],
    [el("tone-saturation"), el("tone-saturation-out"), "saturation"],
  ];
  const toneReset = el<HTMLButtonElement>("tone-reset");

  let currentBbox: BoundingBox | null = null;

  // Metadata for the most-recent generation in this tab. Set when
  // generate completes or when a draft with lastResult is restored.
  // Used by the "Regenerate SVG" button so it can rebuild the overlay
  // against the same bbox/zoom/rotation that produced the JPEGs.
  interface JobMeta {
    jobId: string;
    zoom: 17 | 18;
    bbox: BoundingBox;
    corners?: { nw: [number, number]; ne: [number, number]; se: [number, number]; sw: [number, number] };
    bearing?: number;
    previewWidth?: number;
    previewHeight?: number;
  }
  let currentJobMeta: JobMeta | null = null;

  // Landmark state — keyed by OSM id so we can persist selection across pans.
  const selectedLandmarks = new Map<string, Landmark>();
  let lastFetchedLandmarks: Landmark[] = [];
  let landmarkFetchAbort: AbortController | null = null;
  // Bbox captured at the moment of the last successful fetch — used to
  // highlight the "Load for view" button when the map has moved since.
  let lastFetchedBbox: BoundingBox | null = null;

  // User-edited short names, keyed by OSM id. Persisted so they survive
  // pan/zoom re-fetches and page reloads. An empty string clears the custom
  // name and reverts to the original OSM name.
  const CUSTOM_NAMES_KEY = "landmarkCustomNames";
  const customNames: Map<string, string> = (() => {
    try {
      const raw = localStorage.getItem(CUSTOM_NAMES_KEY);
      return raw ? new Map(Object.entries(JSON.parse(raw))) : new Map();
    } catch {
      return new Map();
    }
  })();

  function persistCustomNames() {
    try {
      localStorage.setItem(
        CUSTOM_NAMES_KEY,
        JSON.stringify(Object.fromEntries(customNames)),
      );
    } catch {
      /* quota / private mode — fine to ignore */
    }
  }

  /** Returns the user-edited short name if one exists, else the OSM name. */
  function displayName(l: { id: string; name: string }): string {
    return customNames.get(l.id) || l.name;
  }

  // ─── Per-draft landmark cache (localStorage) ──────────────────────────
  // Stores the fetched + ranked landmark list per draft so switching
  // between drafts doesn't require a new Overpass fetch.
  const DRAFT_LANDMARKS_KEY = "draftLandmarks";

  function saveDraftLandmarks(draftId: string) {
    try {
      const store = JSON.parse(localStorage.getItem(DRAFT_LANDMARKS_KEY) || "{}");
      store[draftId] = lastFetchedLandmarks;
      localStorage.setItem(DRAFT_LANDMARKS_KEY, JSON.stringify(store));
    } catch { /* quota / private mode */ }
  }

  function loadDraftLandmarks(draftId: string): Landmark[] | null {
    try {
      const store = JSON.parse(localStorage.getItem(DRAFT_LANDMARKS_KEY) || "{}");
      const arr = store[draftId];
      return Array.isArray(arr) && arr.length > 0 ? arr : null;
    } catch {
      return null;
    }
  }

  function clearLandmarkState() {
    lastFetchedLandmarks = [];
    selectedLandmarks.clear();
    lastFetchedBbox = null;
    landmarksStatus.textContent = "";
    renderLandmarkList();
    syncMarkers();
    updateLoadButtonState();
  }

  // User-dragged label offsets, keyed by OSM id. Pixel deltas from the
  // landmark's true lat/lon anchor. Persisted so they survive pan/zoom
  // refetches, page reloads, and re-selections.
  const LABEL_OFFSETS_KEY = "landmarkLabelOffsets";
  const labelOffsets: Map<string, [number, number]> = (() => {
    try {
      const raw = localStorage.getItem(LABEL_OFFSETS_KEY);
      if (!raw) return new Map();
      const parsed = JSON.parse(raw) as Record<string, [number, number]>;
      return new Map(Object.entries(parsed));
    } catch {
      return new Map();
    }
  })();

  function persistLabelOffsets() {
    try {
      localStorage.setItem(
        LABEL_OFFSETS_KEY,
        JSON.stringify(Object.fromEntries(labelOffsets)),
      );
    } catch {
      /* quota / private mode — fine to ignore */
    }
  }

  // ─── Viewport-driven bbox (landmarks are fetched manually) ─────────────
  handle.onBoundsChange((bbox) => {
    currentBbox = bbox;
    bboxOutput.textContent =
      `S ${bbox.lat_min.toFixed(4)}, N ${bbox.lat_max.toFixed(4)}, ` +
      `W ${bbox.lon_min.toFixed(4)}, E ${bbox.lon_max.toFixed(4)}`;
    refreshEnable();
    updateLoadButtonState();
  });

  /** Flag the Load button as stale if the map has moved since the last fetch. */
  function updateLoadButtonState() {
    const stale = !lastFetchedBbox || !sameBbox(currentBbox, lastFetchedBbox);
    landmarksLoad.classList.toggle("stale", stale);
    landmarksLoad.title = stale
      ? "Map has moved — click to refresh landmarks for the current view"
      : "Landmarks are up-to-date for the current view";
  }

  function sameBbox(a: BoundingBox | null, b: BoundingBox | null): boolean {
    if (!a || !b) return false;
    return (
      a.lat_min === b.lat_min &&
      a.lat_max === b.lat_max &&
      a.lon_min === b.lon_min &&
      a.lon_max === b.lon_max
    );
  }

  const refreshEnable = () => {
    const zoom = Number(zoomSelect.value);
    const hasName = Boolean(nameInput.value.trim());
    let canGenerate = Boolean(currentBbox && hasName);
    if (currentBbox) {
      const tiles = estimateTiles(currentBbox, zoom);
      if (tiles > MAX_TILES) {
        tileEstimate.textContent = `~${tiles.toLocaleString()} tiles — too large (max ${MAX_TILES}). Zoom in or reduce area.`;
        canGenerate = false;
      } else if (tiles > 1000) {
        const estMin = Math.ceil(tiles / 200); // rough: ~200 tiles/min + encoding
        tileEstimate.textContent = `~${tiles.toLocaleString()} tiles at zoom ${zoom} (~${estMin} min)`;
      } else {
        tileEstimate.textContent = `~${tiles.toLocaleString()} tiles at zoom ${zoom}`;
      }
    }
    generateBtn.disabled = !canGenerate;
    saveDraftBtn.disabled = !hasName;
  };

  // ─── Red poster title tracks the name input ────────────────────────────
  const syncTitle = () => {
    posterTitle.textContent = nameInput.value.trim().toUpperCase();
  };
  nameInput.addEventListener("input", () => {
    syncTitle();
    refreshEnable();
  });
  zoomSelect.addEventListener("change", refreshEnable);
  syncTitle();

  // ─── Tone sliders (persisted to localStorage) ──────────────────────────
  const TONE_STORAGE_KEY = "posterTone";
  let tone: PosterTone = loadTone();

  function loadTone(): PosterTone {
    try {
      const saved = localStorage.getItem(TONE_STORAGE_KEY);
      if (!saved) return { ...DEFAULT_POSTER_TONE };
      const parsed = JSON.parse(saved) as Partial<PosterTone>;
      return { ...DEFAULT_POSTER_TONE, ...parsed };
    } catch {
      return { ...DEFAULT_POSTER_TONE };
    }
  }

  function saveTone() {
    try {
      localStorage.setItem(TONE_STORAGE_KEY, JSON.stringify(tone));
    } catch {
      /* quota errors are fine to ignore */
    }
  }

  function syncToneUI() {
    for (const [input, output, key] of toneSliders) {
      const v = tone[key];
      input.value = String(v);
      output.textContent = v.toFixed(2);
    }
  }

  // Apply saved tone to the map on startup.
  syncToneUI();
  handle.setTone(tone);

  for (const [input, output, key] of toneSliders) {
    input.addEventListener("input", () => {
      const v = Number(input.value);
      tone = { ...tone, [key]: v };
      output.textContent = v.toFixed(2);
      handle.setTone({ [key]: v });
      saveTone();
    });
  }

  toneReset.addEventListener("click", () => {
    tone = { ...DEFAULT_POSTER_TONE };
    syncToneUI();
    handle.setTone(tone);
    saveTone();
  });

  // ─── Text size sliders (title + landmarks, independent) ─────────────────
  // Each writes to its own CSS custom property so the preview title and
  // landmark labels scale independently. Persisted to localStorage.
  const TITLE_SIZE_KEY = "titleScale";
  const LANDMARK_SIZE_KEY = "landmarkScale";

  function applyTitleScale(v: number) {
    document.documentElement.style.setProperty("--title-scale", String(v));
    titleSizeOut.textContent = `${v.toFixed(2)}×`;
  }
  function applyLandmarkScale(v: number) {
    document.documentElement.style.setProperty("--landmark-scale", String(v));
    landmarkSizeOut.textContent = `${v.toFixed(2)}×`;
  }

  const loadedTitleScale = Number(localStorage.getItem(TITLE_SIZE_KEY) ?? "1");
  const initTitle = Number.isFinite(loadedTitleScale) && loadedTitleScale > 0 ? loadedTitleScale : 1;
  titleSizeSlider.value = String(initTitle);
  applyTitleScale(initTitle);

  const loadedLandmarkScale = Number(localStorage.getItem(LANDMARK_SIZE_KEY) ?? "1");
  const initLandmark = Number.isFinite(loadedLandmarkScale) && loadedLandmarkScale > 0 ? loadedLandmarkScale : 1;
  landmarkSizeSlider.value = String(initLandmark);
  applyLandmarkScale(initLandmark);

  titleSizeSlider.addEventListener("input", () => {
    const v = Number(titleSizeSlider.value);
    applyTitleScale(v);
    try { localStorage.setItem(TITLE_SIZE_KEY, String(v)); } catch { /* */ }
  });
  landmarkSizeSlider.addEventListener("input", () => {
    const v = Number(landmarkSizeSlider.value);
    applyLandmarkScale(v);
    try { localStorage.setItem(LANDMARK_SIZE_KEY, String(v)); } catch { /* */ }
  });

  // ─── Text color pickers (title + landmarks, independent) ──────────────
  const TITLE_COLOR_KEY = "titleColor";
  const LANDMARK_COLOR_KEY = "landmarkColor";
  const DEFAULT_TEXT_COLOR = "#ef4444";

  function applyTitleColor(c: string) {
    document.documentElement.style.setProperty("--title-color", c);
    titleColorInput.value = c;
  }
  function applyLandmarkColor(c: string) {
    document.documentElement.style.setProperty("--landmark-color", c);
    landmarkColorInput.value = c;
  }

  applyTitleColor(localStorage.getItem(TITLE_COLOR_KEY) || DEFAULT_TEXT_COLOR);
  applyLandmarkColor(localStorage.getItem(LANDMARK_COLOR_KEY) || DEFAULT_TEXT_COLOR);

  titleColorInput.addEventListener("input", () => {
    applyTitleColor(titleColorInput.value);
    try { localStorage.setItem(TITLE_COLOR_KEY, titleColorInput.value); } catch { /* */ }
  });
  landmarkColorInput.addEventListener("input", () => {
    applyLandmarkColor(landmarkColorInput.value);
    try { localStorage.setItem(LANDMARK_COLOR_KEY, landmarkColorInput.value); } catch { /* */ }
  });

  // ─── B&W toggle ────────────────────────────────────────────────────────
  // Defaults to checked in HTML; sync class to current state on startup.
  frame.classList.toggle("bw", bwToggle.checked);
  bwToggle.addEventListener("change", () => {
    frame.classList.toggle("bw", bwToggle.checked);
  });

  // ─── Margin toggle (simulates 15 mm print border) ──────────────────────
  // Defaults to checked; toggling changes frame padding → map canvas shrinks,
  // so we need to call resize() to let Mapbox re-measure after the transition.
  frame.classList.toggle("with-margin", marginToggle.checked);
  marginToggle.addEventListener("change", () => {
    frame.classList.toggle("with-margin", marginToggle.checked);
    requestAnimationFrame(() => {
      handle.resize();
      currentBbox = handle.getBounds();
      bboxOutput.textContent =
        `S ${currentBbox.lat_min.toFixed(4)}, N ${currentBbox.lat_max.toFixed(4)}, ` +
        `W ${currentBbox.lon_min.toFixed(4)}, E ${currentBbox.lon_max.toFixed(4)}`;
      refreshEnable();
    });
  });

  // ─── Labels toggle ─────────────────────────────────────────────────────
  // Shows Mapbox place names/roads so the user can find the city, then hides
  // them for a clean preview. Also re-applies the satellite-v9 style = what
  // the Python script actually downloads for the poster.
  labelsToggle.addEventListener("change", () => {
    handle.setLabelsVisible(labelsToggle.checked);
  });

  // ─── Lock toggle ───────────────────────────────────────────────────────
  // Once the frame is perfect, lock pan/zoom/rotate. Orientation toggle is
  // disabled too so we don't silently mutate the bbox under the lock.
  lockToggle.addEventListener("change", () => {
    const locked = lockToggle.checked;
    handle.setLocked(locked);
    frame.classList.toggle("locked", locked);
    orientationBtns.forEach((b) => {
      b.disabled = locked;
    });
  });

  // ─── Orientation toggle ────────────────────────────────────────────────
  orientationBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      if (lockToggle.checked) return;
      const orientation = btn.dataset.orientation;
      if (!orientation) return;
      orientationBtns.forEach((b) => b.classList.toggle("active", b === btn));
      frame.classList.toggle("landscape", orientation === "landscape");
      frame.classList.toggle("portrait", orientation === "portrait");
      requestAnimationFrame(() => {
        handle.resize();
        currentBbox = handle.getBounds();
        bboxOutput.textContent =
          `S ${currentBbox.lat_min.toFixed(4)}, N ${currentBbox.lat_max.toFixed(4)}, ` +
          `W ${currentBbox.lon_min.toFixed(4)}, E ${currentBbox.lon_max.toFixed(4)}`;
        refreshEnable();
        updateLoadButtonState();
      });
    });
  });

  // ─── Landmark fetching (manual — via the "Load for view" button) ──────
  // Previously this was auto-triggered on every moveend with a 700 ms debounce
  // which, combined with Paris-scale queries of 3000+ landmarks, hammered
  // Overpass and frequently hit its rate limits. Now the fetch only runs
  // when the user explicitly clicks the Load button.
  landmarksLoad.addEventListener("click", () => {
    if (!currentBbox) return;
    doLandmarkFetch(currentBbox);
  });

  // ─── Manual landmark placement (click-to-place on map) ─────────────────
  // User clicks "+ Add" → map cursor becomes crosshair → next map click
  // places a custom landmark → prompt for name → added to selectedLandmarks
  // and rendered immediately. Custom landmarks use "custom/{uuid}" as id so
  // they don't collide with OSM ids.
  let placingLandmark = false;

  landmarksAdd.addEventListener("click", () => {
    if (placingLandmark) {
      cancelPlacement();
      return;
    }
    placingLandmark = true;
    landmarksAdd.textContent = "Click map…";
    landmarksAdd.classList.add("stale"); // visual feedback (red outline)
    handle.map.getCanvas().style.cursor = "crosshair";
  });

  function cancelPlacement() {
    placingLandmark = false;
    landmarksAdd.textContent = "+ Add";
    landmarksAdd.classList.remove("stale");
    handle.map.getCanvas().style.cursor = "";
  }

  handle.map.on("click", (e: mapboxgl.MapMouseEvent) => {
    if (!placingLandmark) return;
    const { lng, lat } = e.lngLat;
    const name = window.prompt("Landmark name:");
    if (!name || !name.trim()) {
      cancelPlacement();
      return;
    }
    const id = `custom/${crypto.randomUUID()}`;
    const landmark: Landmark = {
      id,
      name: name.trim(),
      lat,
      lon: lng,
      notable: false,
    };
    selectedLandmarks.set(id, landmark);
    cancelPlacement();
    syncMarkers();
    renderLandmarkList();
  });

  // Escape key cancels placement mode
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && placingLandmark) {
      cancelPlacement();
    }
  });

  async function doLandmarkFetch(bbox: BoundingBox) {
    if (landmarkFetchAbort) landmarkFetchAbort.abort();
    landmarkFetchAbort = new AbortController();
    const signal = landmarkFetchAbort.signal;
    landmarksLoad.disabled = true;
    landmarksStatus.textContent = "Loading…";
    try {
      // Phase 1: Overpass query — show results immediately.
      const base = await fetchLandmarks(
        bbox.lat_min,
        bbox.lat_max,
        bbox.lon_min,
        bbox.lon_max,
        signal,
      );
      lastFetchedLandmarks = sortLandmarks(base).slice(0, DISPLAY_LANDMARK_CAP);
      landmarksStatus.textContent =
        base.length > DISPLAY_LANDMARK_CAP
          ? `Top ${lastFetchedLandmarks.length} of ${base.length} — ranking…`
          : `${base.length} found — ranking…`;
      renderLandmarkList();
      syncMarkers();

      // Phase 2: Wikidata ranking. Rank against the FULL list (so famous
      // landmarks deep in the alphabet aren't missed) but only display the
      // top N in the checklist.
      const ranked = await rankByFame(base, signal);
      if (signal.aborted) return;
      lastFetchedLandmarks = sortLandmarks(ranked).slice(0, DISPLAY_LANDMARK_CAP);
      const famousCount = lastFetchedLandmarks.filter((l) => l.fameRank).length;
      const displayedCount = lastFetchedLandmarks.length;
      const capped = ranked.length > DISPLAY_LANDMARK_CAP;
      const prefix = capped
        ? `Top ${displayedCount} of ${ranked.length}`
        : `${displayedCount} found`;
      landmarksStatus.textContent = famousCount
        ? `${prefix}, ${famousCount} famous`
        : prefix;
      renderLandmarkList();
      syncMarkers();
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      landmarksStatus.textContent = `error: ${(err as Error).message}`;
    } finally {
      landmarksLoad.disabled = false;
      lastFetchedBbox = { ...bbox };
      updateLoadButtonState();
    }
  }

  // ─── Landmark list UI ──────────────────────────────────────────────────
  function renderLandmarkList() {
    // Combine viewport-found landmarks with any selected-but-off-frame ones,
    // so the user can always see + uncheck their current selection.
    const inViewIds = new Set(lastFetchedLandmarks.map((l) => l.id));
    const offFrameSelected = [...selectedLandmarks.values()].filter(
      (l) => !inViewIds.has(l.id),
    );

    const toRender: Array<{ landmark: Landmark; offFrame: boolean }> = [
      ...lastFetchedLandmarks.map((l) => ({ landmark: l, offFrame: false })),
      ...offFrameSelected.map((l) => ({ landmark: l, offFrame: true })),
    ];

    landmarksList.innerHTML = "";
    if (toRender.length === 0) {
      landmarksList.textContent = "No named landmarks in this area.";
      return;
    }
    for (const { landmark, offFrame } of toRender) {
      const label = document.createElement("label");
      label.className = "landmark-item" + (offFrame ? " off-frame" : "");

      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = selectedLandmarks.has(landmark.id);
      cb.addEventListener("change", () => {
        if (cb.checked) selectedLandmarks.set(landmark.id, landmark);
        else selectedLandmarks.delete(landmark.id);
        syncMarkers();
      });

      label.appendChild(cb);

      // Fame rank (top 10) takes precedence over the notable star.
      if (landmark.fameRank) {
        const badge = document.createElement("span");
        badge.className = "fame-rank";
        badge.textContent = String(landmark.fameRank);
        badge.title = `#${landmark.fameRank} most famous in view (Wikipedia sitelinks)`;
        label.appendChild(badge);
      } else if (landmark.notable) {
        const dot = document.createElement("span");
        dot.className = "notable-dot";
        dot.textContent = "★";
        dot.title = "Has Wikipedia article";
        label.appendChild(dot);
      }

      const name = document.createElement("span");
      name.className = "name";
      const shown = displayName(landmark);
      name.textContent = shown;
      name.title =
        shown === landmark.name
          ? landmark.name + (offFrame ? " (off-frame)" : "")
          : `${shown}  (original: ${landmark.name})`;
      label.appendChild(name);

      // Reset-position button — only rendered when this landmark has a
      // non-zero drag offset. Lets the user recover from "dragged too far"
      // without having to find the label on the map and double-click it.
      const offset = labelOffsets.get(landmark.id);
      if (offset && (offset[0] !== 0 || offset[1] !== 0)) {
        const reset = document.createElement("button");
        reset.type = "button";
        reset.className = "landmark-reset";
        reset.textContent = "↺";
        reset.title = `Reset label position (currently offset ${offset[0].toFixed(0)},${offset[1].toFixed(0)}px)`;
        reset.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          labelOffsets.delete(landmark.id);
          persistLabelOffsets();
          renderLandmarkList();
          syncMarkers();
        });
        label.appendChild(reset);
      }

      // Pencil button — only visible on hover. Opens a simple prompt so
      // the user can shorten the display name (or empty → revert).
      const edit = document.createElement("button");
      edit.type = "button";
      edit.className = "landmark-edit";
      edit.textContent = "✎";
      edit.title = "Rename";
      edit.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const current = customNames.get(landmark.id) ?? landmark.name;
        const input = window.prompt(
          `Short name for "${landmark.name}" (empty to revert):`,
          current,
        );
        if (input === null) return; // cancelled
        const trimmed = input.trim();
        if (!trimmed || trimmed === landmark.name) {
          customNames.delete(landmark.id);
        } else {
          customNames.set(landmark.id, trimmed);
        }
        persistCustomNames();
        renderLandmarkList();
        syncMarkers();
      });
      label.appendChild(edit);

      landmarksList.appendChild(label);
    }
  }

  /**
   * Unified renderer: decides each landmark's display style based on the
   * two toggles + selection state, then hands a flat list to the map.
   * Also drives the bottom-left legend overlay.
   *
   * Selected landmarks get consecutive numbers (1..N) based on insertion
   * order in `selectedLandmarks`. Unselected landmarks (in preview mode)
   * get their position in the sorted viewport list.
   */
  function syncMarkers() {
    const showAll = showAllDotsToggle.checked;
    const legendOn = legendModeToggle.checked;

    const viewportOrder = lastFetchedLandmarks;
    const viewportIndex = new Map(viewportOrder.map((l, i) => [l.id, i]));

    const selectedArr = [...selectedLandmarks.values()];
    const selectedNum = new Map(selectedArr.map((l, i) => [l.id, i + 1]));

    const displays: LandmarkDisplay[] = [];
    const seen = new Set<string>();

    // 1. Visit every viewport landmark in its ranked order.
    for (let i = 0; i < viewportOrder.length; i++) {
      const lm = viewportOrder[i];
      seen.add(lm.id);
      const isSelected = selectedLandmarks.has(lm.id);

      if (isSelected) {
        displays.push({
          id: lm.id,
          name: displayName(lm),
          lat: lm.lat,
          lon: lm.lon,
          style: legendOn ? "dot-selected" : "text",
          number: legendOn ? selectedNum.get(lm.id) : undefined,
          offsetPx: labelOffsets.get(lm.id),
        });
      } else if (showAll) {
        displays.push({
          id: lm.id,
          name: displayName(lm),
          lat: lm.lat,
          lon: lm.lon,
          style: i < 10 ? "dot-red" : "dot-yellow",
          number: i + 1,
          // Preview dots stay at their true position (no offset).
        });
      }
    }

    // 2. Off-frame selected landmarks (user panned away) — keep them
    //    visible using the same selected style so the legend stays accurate.
    for (const sel of selectedArr) {
      if (seen.has(sel.id)) continue;
      displays.push({
        id: sel.id,
        name: displayName(sel),
        lat: sel.lat,
        lon: sel.lon,
        style: legendOn ? "dot-selected" : "text",
        number: legendOn ? selectedNum.get(sel.id) : undefined,
        offsetPx: labelOffsets.get(sel.id),
      });
    }

    handle.setLandmarkDisplays(displays);
    renderLegend(legendOn, selectedArr, viewportIndex);
  }

  /** Two-column legend overlay. Only shown when legendMode is on and there's something selected. */
  function renderLegend(
    on: boolean,
    selected: Landmark[],
    _viewportIndex: Map<string, number>,
  ) {
    if (!on || selected.length === 0) {
      posterLegend.classList.add("hidden");
      posterLegend.innerHTML = "";
      return;
    }
    posterLegend.classList.remove("hidden");
    posterLegend.innerHTML = "";
    selected.forEach((l, i) => {
      const entry = document.createElement("div");
      entry.className = "legend-entry";
      const num = document.createElement("span");
      num.className = "legend-num";
      num.textContent = String(i + 1);
      const nm = document.createElement("span");
      nm.textContent = displayName(l);
      entry.appendChild(num);
      entry.appendChild(nm);
      posterLegend.appendChild(entry);
    });
  }

  showAllDotsToggle.addEventListener("change", syncMarkers);
  legendModeToggle.addEventListener("change", syncMarkers);

  // When the user drags a label, remember the new pixel offset. Map.ts has
  // already applied it visually via marker.setOffset(); we just persist it
  // and re-render the list so the ↺ reset button appears (or disappears
  // when the label is dragged back to its anchor).
  handle.onMarkerMoved((id, offset) => {
    if (offset[0] === 0 && offset[1] === 0) {
      labelOffsets.delete(id);
    } else {
      labelOffsets.set(id, offset);
    }
    persistLabelOffsets();
    renderLandmarkList();
  });

  landmarksClear.addEventListener("click", () => {
    selectedLandmarks.clear();
    syncMarkers();
    renderLandmarkList();
  });

  // ─── Saved posters sidebar (drafts only) ───────────────────────────────
  // Generated posters deliberately DO NOT appear here — generation is a
  // one-off action that produces download links in the result panel below
  // the preview. Users decide what to remember by explicitly saving
  // drafts; generations don't clutter the sidebar.

  interface DraftEntry {
    id: string;
    name: string;
    mapCenter: [number, number];
    mapZoom: number;
    mapBearing?: number;
    orientation: "landscape" | "portrait";
    zoomLevel: 17 | 18;
    tone: PosterTone;
    landmarks: Array<{
      id: string;
      name: string;
      lat: number;
      lon: number;
      offsetPx?: [number, number];
    }>;
    // Preview toggles — all optional so older drafts still load
    showMargin?: boolean;
    bwPreview?: boolean;
    showLabels?: boolean;
    locked?: boolean;
    showAllDots?: boolean;
    legendMode?: boolean;
    // Last successful generation — populated on generate-complete if
    // there's an active draft. Restoring a draft re-populates the
    // result panel from this so users don't need to regenerate after
    // a page reload.
    lastResult?: {
      jobId: string;
      generatedAt: number;
      urls: {
        color: string;
        bw: string;
        color_preview: string;
        bw_preview: string;
        overlay_svg?: string;
      };
      // Enough to rebuild the SVG overlay without a full regen.
      meta?: {
        zoom: 17 | 18;
        bbox: BoundingBox;
        corners?: {
          nw: [number, number];
          ne: [number, number];
          se: [number, number];
          sw: [number, number];
        };
        bearing?: number;
      };
    };
    created: number;
    updated: number;
  }


  // Locally cached state so we can update the sidebar optimistically on
  // save/delete. Workers KV is eventually consistent (~60 s) so a GET
  // immediately after PUT often returns stale data, which caused freshly
  // saved drafts to appear to vanish. With this cache, the UI reflects the
  // user's action instantly; loadGallery still reconciles in the background.
  let cachedDrafts: DraftEntry[] = [];

  async function loadGallery() {
    if (cachedDrafts.length === 0) {
      gallery.innerHTML = '<p class="muted gallery-empty">Loading…</p>';
    }
    try {
      const draftsRes = await fetch(`${WORKER_URL}/api/drafts`, { credentials: "include" });
      if (!draftsRes.ok) throw new Error(`drafts HTTP ${draftsRes.status}`);
      const drafts = (await draftsRes.json()) as DraftEntry[];
      // Merge rather than replace: keep any optimistic entries that haven't
      // propagated to KV yet (cachedDrafts[i] absent from server response).
      const serverIds = new Set(drafts.map((d) => d.id));
      const pending = cachedDrafts.filter((d) => !serverIds.has(d.id));
      cachedDrafts = [...pending, ...drafts];
      renderGallery();
    } catch (err) {
      if (cachedDrafts.length === 0) {
        gallery.innerHTML = `<p class="muted gallery-empty">Error: ${
          err instanceof Error ? err.message : err
        }</p>`;
      }
    }
  }

  function renderGallery() {
    if (cachedDrafts.length === 0) {
      gallery.innerHTML =
        '<p class="muted gallery-empty">No saved posters yet. Click Save to add one.</p>';
      return;
    }
    gallery.innerHTML = "";
    for (const d of cachedDrafts) gallery.appendChild(draftCard(d));
  }

  function draftCard(d: DraftEntry): HTMLElement {
    const item = document.createElement("div");
    item.className = "gallery-item draft";
    item.title = `${d.name} — click to edit`;

    const delBtn = document.createElement("button");
    delBtn.type = "button";
    delBtn.className = "delete-btn";
    delBtn.textContent = "×";
    delBtn.title = "Delete draft";
    delBtn.addEventListener("click", async (e) => {
      e.stopPropagation();
      if (!confirm(`Delete draft "${d.name}"?`)) return;
      await deleteDraft(d.id);
    });
    item.appendChild(delBtn);

    const meta = document.createElement("div");
    meta.className = "meta";
    const name = document.createElement("div");
    name.className = "gallery-name";
    name.textContent = d.name;
    const date = document.createElement("div");
    date.className = "gallery-date";
    date.textContent = formatDate(d.updated);
    meta.appendChild(name);
    meta.appendChild(date);
    item.appendChild(meta);

    item.addEventListener("click", () => restoreDraft(d));
    return item;
  }

  function formatDate(unixSec: number): string {
    const d = new Date(unixSec * 1000);
    const now = Date.now();
    const diffSec = (now - unixSec * 1000) / 1000;
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} h ago`;
    if (diffSec < 604800) return `${Math.floor(diffSec / 86400)} d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  // ─── Save / restore / delete drafts ────────────────────────────────────
  // Track the last-restored draft so Save updates it rather than creating a
  // duplicate. Cleared when the user starts a fresh composition (e.g. city
  // search). If null, Save always creates a new draft.
  let activeDraftId: string | null = null;

  async function saveCurrentDraft() {
    const name = nameInput.value.trim();
    if (!name) return;
    const center = handle.map.getCenter();
    // Reuse the restored draft's id if we're editing; else create new.
    const id = activeDraftId ?? crypto.randomUUID();

    const body = {
      name,
      mapCenter: [center.lng, center.lat] as [number, number],
      mapZoom: handle.map.getZoom(),
      mapBearing: handle.map.getBearing(),
      orientation: frame.classList.contains("portrait") ? "portrait" : "landscape",
      zoomLevel: Number(zoomSelect.value) as 17 | 18,
      tone,
      landmarks: [...selectedLandmarks.values()].map((l) => {
        const off = labelOffsets.get(l.id);
        return {
          id: l.id,
          name: l.name,
          lat: l.lat,
          lon: l.lon,
          ...(off && (off[0] !== 0 || off[1] !== 0) ? { offsetPx: off } : {}),
        };
      }),
      // Preview toggle state — so restoring a draft re-creates the exact
      // composition the user saved, not a partial reset to defaults.
      showMargin: marginToggle.checked,
      bwPreview: bwToggle.checked,
      showLabels: labelsToggle.checked,
      locked: lockToggle.checked,
      showAllDots: showAllDotsToggle.checked,
      legendMode: legendModeToggle.checked,
    };

    saveDraftBtn.disabled = true;
    const prevLabel = saveDraftBtn.textContent;
    saveDraftBtn.textContent = "Saving…";
    try {
      const res = await fetch(`${WORKER_URL}/api/drafts/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const saved = (await res.json()) as DraftEntry;
      activeDraftId = saved.id; // subsequent saves update the same draft
      saveDraftLandmarks(saved.id); // cache fetched list so switching back is instant
      // Optimistic: drop this entry into the cache immediately. KV takes
      // up to ~60 s to propagate, so without this the subsequent refresh
      // would briefly show a stale list that doesn't include this draft.
      cachedDrafts = [saved, ...cachedDrafts.filter((d) => d.id !== saved.id)];
      renderGallery();
      saveDraftBtn.textContent = "Saved ✓";
      setTimeout(() => {
        saveDraftBtn.textContent = prevLabel;
        refreshEnable();
      }, 1200);
      // Background reconcile — harmless if it returns stale data because
      // loadGallery now merges rather than replaces.
      loadGallery();
    } catch (err) {
      saveDraftBtn.textContent = prevLabel;
      alert(`Save failed: ${err instanceof Error ? err.message : err}`);
      refreshEnable();
    }
  }

  function restoreDraft(d: DraftEntry) {
    activeDraftId = d.id; // next Save updates this draft, not creates new
    // 1. Name + zoom level (form fields).
    nameInput.value = d.name;
    syncTitle();
    zoomSelect.value = String(d.zoomLevel);

    // 2. Orientation. Needs to apply before the map center/zoom so the
    //    canvas has the correct aspect ratio when jumpTo runs.
    const wantOrientation = d.orientation;
    orientationBtns.forEach((b) => {
      b.classList.toggle("active", b.dataset.orientation === wantOrientation);
    });
    frame.classList.toggle("landscape", wantOrientation === "landscape");
    frame.classList.toggle("portrait", wantOrientation === "portrait");

    // 3. Preview-chrome toggles. Mirror both the checkbox state and the
    //    actual side-effects (class toggles + map calls) so saved drafts
    //    render exactly as they were saved — not a half-reset that picks up
    //    the user's current session toggle state.
    const setBw = d.bwPreview ?? true;
    bwToggle.checked = setBw;
    frame.classList.toggle("bw", setBw);

    const setMargin = d.showMargin ?? true;
    marginToggle.checked = setMargin;
    frame.classList.toggle("with-margin", setMargin);

    const setLabels = d.showLabels ?? false;
    labelsToggle.checked = setLabels;
    handle.setLabelsVisible(setLabels);

    showAllDotsToggle.checked = d.showAllDots ?? false;
    legendModeToggle.checked = d.legendMode ?? false;

    // 4. Tone.
    tone = { ...DEFAULT_POSTER_TONE, ...d.tone };
    syncToneUI();
    handle.setTone(tone);
    saveTone();

    // 5. Restore landmarks — both selections AND the fetched list.
    // The cached list avoids re-fetching from Overpass on every draft switch.
    selectedLandmarks.clear();
    for (const l of d.landmarks) {
      selectedLandmarks.set(l.id, {
        id: l.id,
        name: l.name,
        lat: l.lat,
        lon: l.lon,
        notable: false,
      });
      if (l.offsetPx) {
        labelOffsets.set(l.id, l.offsetPx);
      }
    }
    persistLabelOffsets();

    // Load cached fetched-landmark list from localStorage (saved with the
    // draft). Falls back to showing just the selected landmarks if no cache.
    const cached = loadDraftLandmarks(d.id);
    if (cached) {
      lastFetchedLandmarks = cached;
      lastFetchedBbox = null; // will show "stale" on Load button
      const famousCount = cached.filter((l) => l.fameRank).length;
      landmarksStatus.textContent = famousCount
        ? `${cached.length} cached, ${famousCount} famous`
        : `${cached.length} cached`;
    } else {
      lastFetchedLandmarks = [];
      lastFetchedBbox = null;
      landmarksStatus.textContent = "Click 'Load for view' to fetch landmarks";
    }
    renderLandmarkList();
    syncMarkers();

    // 6. Pan the map to center/zoom/bearing — after resize so the canvas
    //    dimensions match the newly-applied orientation + margin classes.
    //    Then apply the lock last so disabled interactions don't interfere
    //    with jumpTo.
    // Defer the map work one tick so the orientation/margin class changes
    // above can actually flow through layout before jumpTo measures the
    // canvas. (Using setTimeout rather than rAF — certain browsers/environments
    // throttle rAF unpredictably when it's queued during an event handler.)
    setTimeout(() => {
      try {
        handle.resize();
        handle.map.jumpTo({
          center: d.mapCenter,
          zoom: d.mapZoom,
          bearing: d.mapBearing ?? 0,
        });
      } catch (err) {
        console.error("[restore] resize/jumpTo threw:", err);
      }

      const setLocked = d.locked ?? false;
      lockToggle.checked = setLocked;
      handle.setLocked(setLocked);
      frame.classList.toggle("locked", setLocked);
      orientationBtns.forEach((b) => {
        b.disabled = setLocked;
      });
    }, 0);

    // Re-open the result panel if this draft has a saved generation.
    // Otherwise hide it — previous draft's result shouldn't bleed through.
    if (d.lastResult) {
      populateResults(d.lastResult.urls);
      resultsSection.classList.remove("hidden");
      // Rehydrate currentJobMeta so the "Regenerate SVG" button works
      // after a page reload. If the saved draft predates meta storage
      // (older schema), leave it null — the button will stay disabled
      // for those drafts until the next full regenerate repopulates it.
      if (d.lastResult.meta) {
        currentJobMeta = {
          jobId: d.lastResult.jobId,
          zoom: d.lastResult.meta.zoom,
          bbox: d.lastResult.meta.bbox,
          corners: d.lastResult.meta.corners,
          bearing: d.lastResult.meta.bearing,
        };
      } else {
        currentJobMeta = null;
      }
    } else {
      resultsSection.classList.add("hidden");
      currentJobMeta = null;
    }
    updateRegenSvgButton();
  }

  async function deleteDraft(id: string) {
    if (activeDraftId === id) activeDraftId = null;
    // Optimistic: remove from cache + re-render. If the DELETE fails we'll
    // restore below.
    const before = cachedDrafts;
    cachedDrafts = cachedDrafts.filter((d) => d.id !== id);
    renderGallery();
    try {
      const res = await fetch(`${WORKER_URL}/api/drafts/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
      loadGallery(); // background reconcile
    } catch (err) {
      cachedDrafts = before;
      renderGallery();
      alert(`Delete failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  saveDraftBtn.addEventListener("click", saveCurrentDraft);
  refreshGallery.addEventListener("click", loadGallery);
  loadGallery();
  updateRegenSvgButton(); // disabled until first generation in this session

  // ─── City search (Mapbox Geocoding) ────────────────────────────────────
  let searchAbort: AbortController | null = null;
  let searchDebounce: number | null = null;

  citySearch.addEventListener("input", () => {
    if (searchDebounce) window.clearTimeout(searchDebounce);
    const q = citySearch.value.trim();
    if (!q) {
      hideSearchResults();
      return;
    }
    searchDebounce = window.setTimeout(() => runSearch(q), SEARCH_DEBOUNCE_MS);
  });

  citySearch.addEventListener("focus", () => {
    if (citySearch.value.trim() && searchResults.children.length > 0) {
      searchResults.classList.remove("hidden");
    }
  });

  // Hide dropdown when clicking elsewhere on the page.
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest(".search-box")) hideSearchResults();
  });

  citySearch.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      hideSearchResults();
      citySearch.blur();
    }
  });

  async function runSearch(q: string) {
    if (searchAbort) searchAbort.abort();
    searchAbort = new AbortController();
    showSearchMessage("Searching…", "search-loading");
    try {
      const results = await searchCities(q, searchAbort.signal);
      if (results.length === 0) {
        showSearchMessage("No places found.", "search-empty");
      } else {
        renderSearchResults(results);
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      showSearchMessage(
        `Error: ${err instanceof Error ? err.message : err}`,
        "search-empty",
      );
    }
  }

  function renderSearchResults(results: CityResult[]) {
    searchResults.innerHTML = "";
    for (const r of results) {
      const item = document.createElement("div");
      item.className = "search-result";
      item.setAttribute("role", "option");

      const name = document.createElement("div");
      name.className = "place-name";
      name.textContent = r.name;

      const context = document.createElement("div");
      context.className = "place-context";
      context.textContent = r.context;

      item.appendChild(name);
      item.appendChild(context);

      item.addEventListener("click", () => selectCity(r));
      searchResults.appendChild(item);
    }
    searchResults.classList.remove("hidden");
  }

  function showSearchMessage(text: string, cls: "search-loading" | "search-empty") {
    searchResults.innerHTML = `<div class="${cls}">${text}</div>`;
    searchResults.classList.remove("hidden");
  }

  function hideSearchResults() {
    searchResults.classList.add("hidden");
  }

  function selectCity(city: CityResult) {
    // Prefer the admin bbox Mapbox returns for cities — it auto-scales to
    // match the city size (Paris ≈ 10 km across, Porto ≈ 4 km). fitBounds
    // picks an appropriate zoom within maxZoom. Fall back to flyTo + fixed
    // zoom if the result lacks a bbox (rare, typically smaller localities).
    if (city.bbox) {
      handle.map.fitBounds(
        [
          [city.bbox[0], city.bbox[1]],
          [city.bbox[2], city.bbox[3]],
        ],
        { duration: 1000, padding: 10, maxZoom: 14, essential: true },
      );
    } else {
      handle.map.flyTo({
        center: city.center,
        zoom: CITY_ZOOM,
        duration: 1000,
        essential: true,
      });
    }

    // Always fill the city-name input with the selected city. Prior version
    // only did this when the input was empty; that silently dropped the
    // update on every subsequent search, leaving a stale poster title.
    nameInput.value = city.name;
    syncTitle();
    refreshEnable();

    // Clear search UI + start a fresh composition. Landmarks reset so the
    // user starts with a clean slate for the new city.
    activeDraftId = null;
    clearLandmarkState();
    citySearch.value = "";
    hideSearchResults();
  }

  // ─── Submit ────────────────────────────────────────────────────────────
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (!currentBbox) return;

    generateBtn.disabled = true;
    statusSection.classList.remove("hidden");
    resultsSection.classList.add("hidden");
    statusText.textContent = "Submitting…";
    statusHint.textContent = "";
    progressFill.style.width = "0%";

    // Build the overlay data the Python script needs to bake a companion SVG
    // next to the JPEGs. Use custom short names (via displayName) so the SVG
    // matches what the user saw in the preview — and include any manual
    // drag offsets the user applied to resolve label overlaps.
    //
    // Offsets are captured in CSS pixels at the CURRENT preview size; the
    // Python side also receives the preview dimensions so it can scale the
    // offset to the stitched-image pixel grid (poster scale is much larger).
    const mapContainer = handle.map.getContainer();
    const previewW = mapContainer.clientWidth || 1;
    const previewH = mapContainer.clientHeight || 1;

    const overlayLandmarks = [...selectedLandmarks.values()].map((l) => {
      const offset = labelOffsets.get(l.id);
      return {
        name: displayName(l),
        lat: l.lat,
        lon: l.lon,
        ...(offset && (offset[0] !== 0 || offset[1] !== 0) ? { offsetPx: offset } : {}),
      };
    });

    // Four visible-frame corners + bearing — if the user rotated the
    // map, these describe the actual rotated rect so Python can QUAD-
    // transform the axis-aligned stitched download into the rotated
    // poster. When bearing=0 these degenerate to the axis-aligned bbox.
    const corners = handle.getFrameCorners();
    const bearing = handle.map.getBearing();

    const body = {
      name: nameInput.value.trim(),
      zoom: Number(zoomSelect.value),
      lat_min: currentBbox.lat_min,
      lat_max: currentBbox.lat_max,
      lon_min: currentBbox.lon_min,
      lon_max: currentBbox.lon_max,
      corners,
      bearing,
      landmarks: overlayLandmarks,
      legendMode: legendModeToggle.checked,
      showMargin: marginToggle.checked,
      titleScale: Number(titleSizeSlider.value) || 1.0,
      titleColor: titleColorInput.value || DEFAULT_TEXT_COLOR,
      landmarkScale: Number(landmarkSizeSlider.value) || 1.0,
      landmarkColor: landmarkColorInput.value || DEFAULT_TEXT_COLOR,
      previewWidth: previewW,
      previewHeight: previewH,
    };

    try {
      const res = await fetch(`${WORKER_URL}/api/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const { job_id } = (await res.json()) as { job_id: string };
      // Remember the meta for later — the overlay regenerate button
      // needs the same zoom/bbox/corners/bearing to line up with the
      // JPEGs that will be produced.
      currentJobMeta = {
        jobId: job_id,
        zoom: body.zoom as 17 | 18,
        bbox: {
          lat_min: body.lat_min,
          lat_max: body.lat_max,
          lon_min: body.lon_min,
          lon_max: body.lon_max,
        },
        corners: body.corners,
        bearing: body.bearing,
        previewWidth: body.previewWidth,
        previewHeight: body.previewHeight,
      };
      await pollUntilDone(job_id);
    } catch (err) {
      statusText.textContent = "Failed to start job.";
      statusHint.textContent = err instanceof Error ? err.message : String(err);
      generateBtn.disabled = false;
    }
  });

  /** Enable/disable the "Regenerate SVG" button based on whether we
   *  have the metadata to do so. Older drafts + fresh sessions before
   *  a first generation have no meta and can't regenerate. */
  function updateRegenSvgButton() {
    const enabled = !!currentJobMeta;
    regenSvgBtn.disabled = !enabled;
    regenSvgBtn.title = enabled
      ? "Rebuilds the SVG overlay with current text/colors/positions. Takes ~2 seconds."
      : "Generate the poster first — the SVG needs a reference frame to align to.";
  }

  regenSvgBtn.addEventListener("click", async () => {
    if (!currentJobMeta) return;
    regenSvgBtn.disabled = true;
    regenSvgStatus.textContent = "Regenerating…";

    try {
      // Build the same overlay body the generate endpoint receives so
      // the SVG mirrors whatever the user has composed right now.
      const mapContainer = handle.map.getContainer();
      const previewW = mapContainer.clientWidth || 1;
      const previewH = mapContainer.clientHeight || 1;
      const overlayLandmarks = [...selectedLandmarks.values()].map((l) => {
        const offset = labelOffsets.get(l.id);
        return {
          name: displayName(l),
          lat: l.lat,
          lon: l.lon,
          ...(offset && (offset[0] !== 0 || offset[1] !== 0) ? { offsetPx: offset } : {}),
        };
      });

      const body = {
        title: nameInput.value.trim() || "POSTER",
        zoom: currentJobMeta.zoom,
        bbox: currentJobMeta.bbox,
        corners: currentJobMeta.corners,
        bearing: currentJobMeta.bearing,
        landmarks: overlayLandmarks,
        legendMode: legendModeToggle.checked,
        titleScale: Number(titleSizeSlider.value) || 1.0,
        titleColor: titleColorInput.value || DEFAULT_TEXT_COLOR,
        landmarkScale: Number(landmarkSizeSlider.value) || 1.0,
        landmarkColor: landmarkColorInput.value || DEFAULT_TEXT_COLOR,
        previewWidth: previewW,
        previewHeight: previewH,
      };

      const res = await fetch(`${WORKER_URL}/api/svg/${currentJobMeta.jobId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || `HTTP ${res.status}`);
      }
      const { url, size } = (await res.json()) as { url: string; size: number };
      downloadOverlay.href = url;
      overlayRow.classList.remove("hidden");
      regenSvgStatus.textContent = `Updated ✓ (${(size / 1024).toFixed(0)} KB)`;
      setTimeout(() => {
        regenSvgStatus.textContent = "";
      }, 3000);

      // Also persist the refreshed URL to the active draft so reload
      // picks up the latest overlay, not the pre-regen one.
      if (activeDraftId) {
        const cached = cachedDrafts.find((d) => d.id === activeDraftId);
        if (cached && cached.lastResult) {
          const updated: DraftEntry = {
            ...cached,
            lastResult: {
              ...cached.lastResult,
              urls: { ...cached.lastResult.urls, overlay_svg: url },
            },
          };
          cachedDrafts = cachedDrafts.map((d) => (d.id === activeDraftId ? updated : d));
          const { id: _id, created: _c, updated: _u, ...putBody } = updated;
          void _id; void _c; void _u;
          fetch(`${WORKER_URL}/api/drafts/${activeDraftId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(putBody),
          }).catch(() => {});
        }
      }
    } catch (err) {
      regenSvgStatus.textContent = `Failed: ${err instanceof Error ? err.message : err}`;
    } finally {
      regenSvgBtn.disabled = !currentJobMeta;
    }
  });

  /** Fill in the thumbnails + download links from a set of job URLs.
   *  Shared between "generation just completed" and "reopen draft with a
   *  saved lastResult" so both paths restore an identical result panel. */
  function populateResults(urls: NonNullable<JobStatus["urls"]>) {
    // ?inline=1 tells the Worker to serve without Content-Disposition:
    // attachment, so <img> thumbnails render inline. Download buttons
    // omit it so the browser saves the full-res JPEG to disk instead
    // of trying to render an 800-megapixel image in a new tab.
    previewColor.src = urls.color_preview + "?inline=1";
    previewBw.src = urls.bw_preview + "?inline=1";
    downloadColor.href = urls.color;
    downloadBw.href = urls.bw;
    if (urls.overlay_svg) {
      downloadOverlay.href = urls.overlay_svg;
      overlayRow.classList.remove("hidden");
    } else {
      overlayRow.classList.add("hidden");
    }
  }

  /** PATCH the active draft with the URLs from a fresh generation. We
   *  PUT the full draft (the KV route is upsert-only) reusing the cached
   *  entry for the non-result fields so no composition state is lost. */
  async function patchActiveDraftLastResult(
    draftId: string,
    urls: NonNullable<JobStatus["urls"]>,
    jobId: string,
  ) {
    const cached = cachedDrafts.find((d) => d.id === draftId);
    if (!cached) return;
    const updated: DraftEntry = {
      ...cached,
      lastResult: {
        jobId,
        generatedAt: Math.floor(Date.now() / 1000),
        urls,
        // Stash the bbox/zoom/corners/bearing so a future "regenerate
        // SVG only" can be done without any full generation context.
        meta: currentJobMeta && currentJobMeta.jobId === jobId
          ? {
              zoom: currentJobMeta.zoom,
              bbox: currentJobMeta.bbox,
              corners: currentJobMeta.corners,
              bearing: currentJobMeta.bearing,
            }
          : undefined,
      },
    };
    // Optimistic local update so subsequent clicks on the same draft
    // card in this tab already have the URLs, even before the KV PUT
    // round-trips.
    cachedDrafts = cachedDrafts.map((d) => (d.id === draftId ? updated : d));
    // Strip server-managed fields the PUT endpoint re-derives.
    const { id: _id, created: _c, updated: _u, ...body } = updated;
    void _id; void _c; void _u;
    try {
      await fetch(`${WORKER_URL}/api/drafts/${draftId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
    } catch (err) {
      console.warn("[draft] failed to persist lastResult:", err);
    }
  }

  async function pollUntilDone(jobId: string) {
    const startedAt = Date.now();
    while (true) {
      await sleep(3000);
      let status: JobStatus;
      try {
        const res = await fetch(`${WORKER_URL}/api/status/${jobId}`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        status = (await res.json()) as JobStatus;
      } catch (err) {
        statusHint.textContent = `polling error: ${err instanceof Error ? err.message : err}`;
        continue;
      }

      renderStatus(status, Date.now() - startedAt);

      if (status.status === "complete" && status.urls) {
        populateResults(status.urls);
        resultsSection.classList.remove("hidden");
        generateBtn.disabled = false;
        // If there's an active draft, persist the URLs so reopening the
        // draft after a reload restores the same result panel without
        // needing to regenerate. If there's no active draft, the URLs
        // live only in this tab until reload — the user can click Save
        // to create one that remembers this generation.
        if (activeDraftId) {
          patchActiveDraftLastResult(activeDraftId, status.urls, jobId);
        }
        // Regen button now has everything it needs.
        updateRegenSvgButton();
        // Note: intentionally do NOT touch the sidebar here — generation
        // is a one-off action, the result panel above already shows the
        // download links. Sidebar is user-managed drafts only.
        return;
      }
      if (status.status === "failed") {
        statusText.textContent = "Job failed.";
        statusHint.textContent = status.error || "Unknown error";
        generateBtn.disabled = false;
        return;
      }
    }
  }

  function renderStatus(status: JobStatus, elapsedMs: number) {
    const elapsedSec = Math.floor(elapsedMs / 1000);
    switch (status.status) {
      case "queued":
        statusText.textContent = "Warming up runner… (5%)";
        statusHint.textContent = `~30–60 s. Elapsed: ${elapsedSec}s.`;
        progressFill.style.width = "5%";
        return;
      case "started":
        statusText.textContent = "Starting tile download… (10%)";
        progressFill.style.width = "10%";
        return;
      case "downloading": {
        const done = status.progress ?? 0;
        const total = status.total ?? 1;
        const pct = Math.min(85, Math.round(10 + (done / total) * 75));
        statusText.textContent = `Downloading tiles: ${done}/${total} (${pct}%)`;
        statusHint.textContent = `Elapsed: ${elapsedSec}s.`;
        progressFill.style.width = `${pct}%`;
        return;
      }
      case "stitching": {
        const done = status.progress ?? 0;
        const total = status.total ?? 0;
        const subPct = total > 0 ? Math.round((done / total) * 100) : 0;
        const pct = total > 0 ? Math.min(94, 85 + Math.round((done / total) * 9)) : 88;
        statusText.textContent = total > 0
          ? `Stitching image: ${subPct}% (${pct}%)`
          : `Stitching image… (88%)`;
        statusHint.textContent = `Elapsed: ${elapsedSec}s.`;
        progressFill.style.width = `${pct}%`;
        return;
      }
      case "encoding": {
        // Sub-phases during JPEG encoding (each 30-60 s for a full poster).
        const stepLabel = status.detail || "Encoding JPEG…";
        const stepMap: Record<string, number> = { color: 91, bw: 93, previews: 94 };
        const pct = stepMap[status.step ?? ""] ?? 92;
        statusText.textContent = `${stepLabel} (${pct}%)`;
        statusHint.textContent = `Elapsed: ${elapsedSec}s.`;
        progressFill.style.width = `${pct}%`;
        return;
      }
      case "uploading":
        statusText.textContent = "Uploading to storage… (95%)";
        statusHint.textContent = `Elapsed: ${elapsedSec}s.`;
        progressFill.style.width = "95%";
        return;
      case "complete":
        statusText.textContent = "Done! (100%)";
        progressFill.style.width = "100%";
        return;
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

bootstrap();
